import React, { Component } from 'react';
import { connect } from 'react-redux';
import NoContentBox from '../../components/common/NoContentBox';
import { Accordion, AccordionItem, Loading } from 'carbon-components-react';
import {
  getPsAiSummary,
  searchAiInsights,
  getAiInsightsStatusById,
} from '../../actions/CandidateInfo';
import { BsStars } from 'react-icons/bs';
import _ from 'lodash';
import SuccErrNotification from '../../components/private/SuccErrNotification';
import ChatGPTSearchBox from '../../components/common/ChatGPTSearchBox';
import { lowerAndTrim } from '../../utils/helpers';

const renderHtml = (html) => {
  return (
    <div
      style={{ lineHeight: 1.3 }}
      className="xpa-ul pr-3"
      dangerouslySetInnerHTML={{ __html: html.replace(/\n/g, '<br />') }}
    />
  );
};

const orderedSections = [
  { key: 'executive_summary', title: 'Executive Summary' },
  { key: 'leadership_qualities', title: 'Leadership Qualities' },
  { key: 'intellectual_qualities', title: 'Intellectual Qualities' },
  { key: 'character_values', title: 'Character & Values' },
  { key: 'people_skills', title: 'People Skills' },
  { key: 'communication_skills', title: 'Communication Skills' },
  { key: 'personal_effectiveness', title: 'Personal Effectiveness' },
  { key: 'peer_feedback_summary', title: 'Peer Feedback Summary' },
];

// Special rendering for peer feedback summary
const renderPeerFeedback = (feedback) => (
  <div
    className="peer-feedback-container"
    style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
    <div className="feedback-section">
      <h6 className="feedback-subtitle">Positive Feedback:</h6>
      <div className="feedback-content">{feedback.positive}</div>
    </div>
    <div className="feedback-section mt-2">
      <h6 className="feedback-subtitle">Areas for Improvement:</h6>
      <div className="feedback-content">{feedback.areas_for_improvement}</div>
    </div>
  </div>
);

const RenderAccordionFromReport = ({ reportData = {} }) => {
  return (
    <Accordion align="start" className="mb-3">
      {orderedSections
        .filter((section) => reportData[section.key])
        .map((section) => {
          const content =
            section.key === 'peer_feedback_summary' ? (
              renderPeerFeedback(reportData[section.key])
            ) : (
              <div style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {reportData[section.key]}
              </div>
            );

          return (
            <AccordionItem
              key={section.key} // Using the unique key from our orderedSections
              className="gba-accor-content mt-2"
              title={
                <div className="bx--row align-items-center">
                  <div className="h6 font-weight-bold mb-1 bx--col-lg-12 bx--col-md-12">
                    {section.title}
                  </div>
                </div>
              }>
              {content}
            </AccordionItem>
          );
        })}
    </Accordion>
  );
};

class PresidentScholarshipAISummary extends Component {
  constructor(props) {
    super(props);
    const { appId } = props || {};
    this.state = {
      evaluationResult: JSON.parse(
        sessionStorage.getItem(`evaluationResult-${appId}`)
      ),
    };
    this.resultRef = React.createRef();
  }

  attempts = 0;

  componentDidMount() {
    this.props.onRef && this.props.onRef(this);
    this.renderOnMount();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.taskId && this.state.taskId !== prevState.taskId) {
      this.handleGetTaskStatus();
    }

    if (
      prevState.evaluationResult !== this.state.evaluationResult &&
      this.resultRef.current
    ) {
      this.resultRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  componentWillUnmount() {
    this.props.onRef && this.props.onRef(undefined);
    this.attempts = 12;
  }

  renderOnMount = () => {
    const { appId } = this.props;
    if (appId) {
      this.props.getPsAiSummary(appId);
    }
  };

  handleOpenAccr = (name) => {
    this.setState({
      isOpenAccrVal: name,
    });
  };

  handleCloseNotifBtn = () => {
    this.setState({
      successNotif: false,
      errorNotif: false,
      notifMsg: '',
    });
  };

  handleGetTaskStatus = async () => {
    const { appId } = this.props;
    const { taskId } = this.state;
    if (!taskId) return;

    const maxAttempts = 20;
    this.attempts = 0;

    while (this.attempts < maxAttempts) {
      this.attempts++;
      try {
        const res = await this.props.getAiInsightsStatusById(taskId);
        if (res && !res.error) {
          const { task_status, task_result } = res || {};
          const { custom_evaluation } = task_result || {};
          const { evaluation_result } = custom_evaluation || {};
          const finalTaskStatus = lowerAndTrim(task_status);
          if (finalTaskStatus === 'success') {
            this.setState({
              evaluationResult: evaluation_result,
              isAIGeneratedPromptAvail: true,
              taskId: null,
              errorNotif: false,
              successNotif: true,
              notifMsg: 'Generated Successfully!',
            });
            sessionStorage.setItem(
              `evaluationResult-${appId}`,
              JSON.stringify(evaluation_result)
            );
            break;
          } else if (finalTaskStatus !== 'success') {
            await new Promise((resolve) => setTimeout(resolve, 5000)); // 5 seconds delay
          } else {
            this.setState({
              taskId: null,
              errorNotif: true,
              successNotif: false,
              errMsg: 'We have encountered something, Please try again.',
              isAIGeneratedPromptAvail: false,
            });
            break;
          }
        } else {
          this.setState({
            taskId: null,
            errorNotif: true,
            successNotif: false,
            errMsg:
              res?.error?.message ||
              'Failed to Generate. Please try again later',
            isAIGeneratedPromptAvail: false,
          });
          break;
        }
      } catch (error) {
        console.error('ERROR::', error);
        this.setState({
          taskId: null,
          errorNotif: true,
          successNotif: false,
          errMsg:
            error?.message || 'Failed to Generate. Please try again later',
        });
        break;
      }
    }

    if (this.attempts >= maxAttempts) {
      this.setState({
        taskId: null,
        errorNotif: true,
        successNotif: false,
        errMsg:
          'Oops! It looks like this is taking longer than usual. Please try again later',
      });
    }
  };

  handleSubmitPrompt = (prompt) => {
    const { appId } = this.props || {};
    const data = {
      appId,
      prompt,
    };
    sessionStorage.setItem(`searchQuery-${appId}`, prompt);
    this.props
      .searchAiInsights(data)
      .then((res) => {
        const { task_id } = res || {};
        this.setState({
          taskId: task_id,
          isAIGeneratedPromptAvail: false,
          successNotif: false,
          errorNotif: false,
        });
      })
      .catch((error) => console.log('Error', error));
  };

  render() {
    const {
      successNotif,
      errorNotif,
      notifMsg,
      errMsg,
      taskId,
      evaluationResult,
      isAIGeneratedPromptAvail,
    } = this.state;
    const { psAiSummary } = this.props;
    const { pssbReport } = psAiSummary || {};
    return (
      <div>
        <div className="d-flex">
          <div className="">
            <div className="mt-2 h6 font-weight-bold">(BETA) AI Summary</div>
          </div>
          <span className="xpa-gen-ai ml-3" onClick={() => {}}>
            <BsStars
              size={20}
              style={{ color: 'rgb(255 191 0)', marginRight: '2px' }}
            />
            Generated using AI
          </span>
        </div>
        <hr />

        {!_.isEmpty(pssbReport) ? (
          <div>
            <RenderAccordionFromReport reportData={pssbReport} />
            <>
              <hr />
              <div className="mb-5">
                <SuccErrNotification
                  successNotif={successNotif}
                  errorNotif={errorNotif}
                  notifMsg={notifMsg}
                  handleCloseNotifBtn={this.handleCloseNotifBtn}
                  errMsg={errMsg}
                />
                <div className="mb-2 font-weight-bold h2">
                  What would you like to know about this candidate?
                </div>
                <ChatGPTSearchBox
                  handleSubmit={this.handleSubmitPrompt}
                  taskId={taskId}
                  isAIGeneratedPromptAvail={isAIGeneratedPromptAvail}
                />

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {taskId && (
                    <>
                      <Loading
                        description="Active loading indicator"
                        small={true}
                        withOverlay={false}
                        style={{ marginRight: '10px' }}
                      />
                      <span>Loading data, please wait...</span>
                    </>
                  )}
                </div>

                <div ref={this.resultRef} style={{ scrollMarginTop: 400 }}>
                  {evaluationResult && (
                    <div className="mb-5 mt-3">
                      <h5 className="h6 mb-1 font-weight-bold">
                        Generative AI analysis of your query about this
                        candidate :
                      </h5>
                      <div>{renderHtml(evaluationResult)}</div>
                    </div>
                  )}
                </div>
              </div>
            </>
          </div>
        ) : (
          <NoContentBox
            hideTile
            message="There are no AI Insights for your current selection"
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  psAiSummary: state.CandidateInfo.psAiSummary,
});

const mapDispatchToProps = {
  getPsAiSummary,
  searchAiInsights,
  getAiInsightsStatusById,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PresidentScholarshipAISummary);
